import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useAreRequiredFieldsFilled from './useAreRequiredFieldsFilled.tsx';
import useFieldValidator from './useFieldValidator.tsx';
import useNavigateTo from './useNavigateTo.tsx';
import { useRegistrationStore } from '../store/store.ts';
import { TFlow } from '../types/types.ts';
import { TCountryMarketInfo } from '../utils/countries.ts';
import { sendGtmEvent } from '../utils/gatekeeper.ts';

const useFlowNavigation = (marketInfo: TCountryMarketInfo, config: TFlow) => {
  const {
    initializeFormData,
    formData,
    initializeMetadata,
    metadata,
    errors,
    setErrors,
    currentMarket,
    setCurrentMarket,
    validateOnRender,
    setValidateOnRender,
  } = useRegistrationStore();
  const { market } = marketInfo;
  const { step: stepParam } = useParams<{ step: string }>();
  const { navigateTo } = useNavigateTo();

  const step = useMemo(() =>
    stepParam
      ? +stepParam.split('_')[1] || 1
      : 1,
  [stepParam]);

  const { validateAllFields } = useFieldValidator(config);

  const isNextEnabled = useAreRequiredFieldsFilled(formData, config.steps[step]);

  const saveToLocalStorage = () => {
    localStorage.setItem(`data_${market}`, JSON.stringify(formData));
    localStorage.setItem(`metadata_${market}`, JSON.stringify(metadata));
  };

  const handleNavigation = (redirectTo?: string) => {
    const stepsSkipped = JSON.parse(localStorage.getItem(`steps_skipped_${market}`) || '{}');

    if (!redirectTo) {
      localStorage.setItem(`steps_skipped_${market}`, JSON.stringify({ ...stepsSkipped, [step]: '1' }));

      return;
    }

    // redirectTo is a step offset only. Never follow it as a URL: it comes from the
    // CMS, and doing so would let a flow edit send players off-site (open redirect).
    if (isNaN(+redirectTo)) {
      console.warn(`Ignoring non-numeric redirectTo "${redirectTo}": only step offsets are supported`);

      return;
    }

    const nextStep = +step + +redirectTo;

    Object.keys(stepsSkipped).forEach((key) => {
      if (+key > step) {
        delete stepsSkipped[key];
      }
    });

    localStorage.setItem(`step_${market}`, nextStep.toString());
    localStorage.setItem(`steps_skipped_${market}`, JSON.stringify({
      ...stepsSkipped,
      [step]: redirectTo,
    }));

    navigateTo(market, nextStep);
  };

  const handleGtmPayload = (gtmPayload?: Record<string, string>) => {
    if (!gtmPayload) return;

    const PASSTHROUGH_KEYS = new Set(['step', 'event', 'action']);

    const updatedGtmPayload: Record<string, unknown> = Object.entries(gtmPayload).reduce(
      (acc, [key, value]) => {
        if (PASSTHROUGH_KEYS.has(key)) {
          return { ...acc, [key]: value };
        }

        let resolvedValue: unknown;

        // Handle boolean conversion prefix (logic for !! prefix defined in the CMS)
        if (value.startsWith('!!')) {
          const lookupKey = value.slice(2);
          const sourceValue = formData?.[lookupKey] ?? metadata?.[lookupKey];
          resolvedValue = Boolean(sourceValue);
        } else {
          resolvedValue = formData?.[value] ?? metadata?.[value] ?? false;
        }

        return { ...acc, [key]: resolvedValue };
      },
      {} as Record<string, unknown>,
    );

    const { action } = updatedGtmPayload;

    sendGtmEvent({ action: action as string, data: updatedGtmPayload });
  };

  const handleNext = ({ gtmPayload, redirectTo, canProceed }: {
    gtmPayload?: Record<string, string>;
    redirectTo?: string;
    canProceed?: boolean;
  }) => {
    if (!isNextEnabled && !canProceed) {
      validateAllFields();

      return;
    }

    saveToLocalStorage();
    handleNavigation(redirectTo);
    setErrors({});
    handleGtmPayload(gtmPayload);
  };

  const handlePrevious = () => {
    const stepsSkipped = JSON.parse(localStorage.getItem(`steps_skipped_${market}`) || '{}');

    const previousSteps = Object.keys(stepsSkipped)
      .map(Number)
      .filter((s) => s < step)
      .sort((a, b) => b - a); // Sort descending to get the last valid step

    const previousStep = previousSteps.length > 0 ? previousSteps[0] : step - 1;
    const previousSkip = stepsSkipped[previousStep] ?? '1';

    navigateTo(market, step - +previousSkip);
  };

  const totalSteps = useMemo(() => Object.keys(config.steps).length, [config.steps]);

  useEffect(() => {
    if(validateOnRender) {
      validateAllFields();
      setValidateOnRender(false);
    }
  }, [setValidateOnRender, validateAllFields, validateOnRender]);

  useEffect(() => {
    if (formData && currentMarket === market) {
      return;
    }

    initializeFormData(marketInfo);
    initializeMetadata(market);
    setErrors({});
    setCurrentMarket(market);
  }, [market, formData, currentMarket, initializeFormData, setCurrentMarket, setErrors, initializeMetadata]);

  return {
    step, totalSteps, handleNext, handlePrevious, errors, formData, handleGtmPayload,
  };
};

export default useFlowNavigation;
